import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from '@companion-professional/components';
import { getErrorMessageFromAxiosError } from '@companion-professional/webutils';
import { getVideoDownloadUrl } from '../lib/api';

// VIDEO_NOT_READY_STATUS is the HTTP status the backend returns when an item exists but its original
// video file hasn't finished landing in storage yet (a rare upload race).
const VIDEO_NOT_READY_STATUS = 409;

// triggerBrowserDownload navigates an off-screen anchor to url to start a download. The pre-signed
// URL already carries a `Content-Disposition: attachment` response header, so the browser downloads
// the file rather than navigating to it (the `download` attribute is ignored for cross-origin URLs).
const triggerBrowserDownload = (url: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

// useDownloadVideo returns a `downloadVideo(itemId)` function that downloads the original uploaded
// video for a checkup request item, plus an `isDownloading` flag. It fetches a short-lived pre-signed
// URL from the backend and then starts a browser download. Failures are surfaced as toasts: a
// friendly "not ready yet" message while the video is still being processed (HTTP 409), or a generic
// error otherwise.
export const useDownloadVideo = () => {
  const { isPending, mutate: downloadVideo } = useMutation({
    mutationFn: getVideoDownloadUrl,
    onSuccess: ({ url }) => triggerBrowserDownload(url),
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === VIDEO_NOT_READY_STATUS) {
        toast.warning('Video not ready yet', {
          description: 'This video is still being processed. Please check back in a few minutes.'
        });
        return;
      }
      toast.error('Download failed', {
        description: getErrorMessageFromAxiosError(error, 'Unable to download the video. Please try again.')
      });
    }
  });

  return { isDownloading: isPending, downloadVideo };
};
